import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Button } from "react-bootstrap";
import { FaArrowDown } from "react-icons/fa";

const HeroSection = ({ language, scrollToSearch }) => {
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchHeroSection = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/hero-section`,
          {
            headers: {
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            },
            withCredentials: true,
          }
        );

        if (!mounted) return;
        if (response.data.success && response.data.data.length > 0) {
          setHero(response.data.data[0]);
        } else {
          setHero(null);
        }
      } catch (error) {
        console.error("Error fetching Hero Section:", error);
        setHero(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchHeroSection();

    return () => {
      mounted = false;
    };
  }, []);

  const extractVideoId = (url) => {
    if (!url) return "";
    const regExp =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regExp);
    return match ? match[1] : "";
  };

  const getFormattedYoutubeUrl = (url) => {
    const videoId = extractVideoId(url);
    if (!videoId) return "";
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${
      isMuted ? 1 : 0
    }&loop=1&playlist=${videoId}&controls=0&showinfo=0&modestbranding=1`;
  };

  return (
    <section
      className={`hero-section d-flex align-items-center justify-content-center text-center text-white position-relative ${
        loading ? "loading" : "loaded"
      }`}
      style={{ height: "800px", overflow: "hidden", paddingTop: "80px" }}
      aria-busy={loading}
      aria-live="polite"
    >
      {loading ? (
        <div className="hero-skeleton w-100 h-100 position-relative">
          <div className="hero-skeleton-video position-absolute top-0 start-0 w-100 h-100" />
          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" />
          <Container className="position-relative hero-skeleton-content">
            <div className="skeleton-line skeleton-title mb-3" />
            <div className="skeleton-line skeleton-subtitle mb-4" />
            <div className="d-flex justify-content-center gap-3">
              <div className="skeleton-btn" />
              <div className="skeleton-btn small" />
            </div>
            <div className="skeleton-arrow mt-4" />
          </Container>
        </div>
      ) : hero ? (
        <>
          {hero.link_youtube ? (
            <div className="position-absolute top-0 start-0 w-100 h-100">
              <iframe
                key={isMuted + (hero.link_youtube || "")}
                src={getFormattedYoutubeUrl(hero.link_youtube)}
                title="YouTube Video Background"
                frameBorder="0"
                allow="autoplay; fullscreen"
                className="video-iframe"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          ) : (
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                backgroundImage: hero.background_image
                  ? `url(${hero.background_image})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "brightness(0.45)",
              }}
            />
          )}

          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" />

          <Container
            className="position-relative text-center"
            style={{ zIndex: 10 }}
          >
            <h2 className="display-4 fw-bold">{hero.title}</h2>
            <p className="lead mb-4">{hero.subtitle}</p>

            <Button
              variant="dark"
              onClick={() => setIsMuted((prev) => !prev)}
              className="mute-toggle-btn"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 200,
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "bold",
                borderRadius: "5px",
              }}
            >
              {isMuted ? "Unmute 🔊" : "Mute 🔇"}
            </Button>

            <div className="d-flex justify-content-center gap-3 mb-3">
              <Button variant="light" size="lg">
                {language === "EN" ? "Book Your Stay" : "Pesan Sekarang"}
              </Button>
            </div>

            <div
              onClick={scrollToSearch}
              className="scroll-down-icon d-flex justify-content-center align-items-center position-absolute"
              style={{
                marginTop: "30px",
                left: "50%",
                transform: "translateX(-50%)",
                cursor: "pointer",
                zIndex: 20,
              }}
              aria-hidden="true"
            >
              <FaArrowDown size={30} className="animate-bounce" />
            </div>
          </Container>
        </>
      ) : (
        <div className="text-center text-white">Hero section not available</div>
      )}

      <style jsx>{`
        /* shimmer */
        .skeleton-line,
        .skeleton-btn,
        .hero-skeleton-video,
        .skeleton-arrow {
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.06) 0%,
            rgba(255, 255, 255, 0.12) 50%,
            rgba(255, 255, 255, 0.06) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 1.4s linear infinite;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        .hero-skeleton-video {
          /* mimic video aspect and position */
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          filter: blur(2px);
        }

        .hero-skeleton-content {
          padding: 120px 0;
          z-index: 20;
        }

        .skeleton-title {
          width: 60%;
          height: 48px;
          margin: 0 auto;
          border-radius: 6px;
        }

        .skeleton-subtitle {
          width: 40%;
          height: 20px;
          margin: 10px auto 24px auto;
          border-radius: 6px;
        }

        .skeleton-btn {
          width: 160px;
          height: 44px;
          border-radius: 6px;
        }

        .skeleton-btn.small {
          width: 120px;
          height: 36px;
        }

        .skeleton-arrow {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          margin: 24px auto 0;
        }

        /* existing bounce animation */
        .scroll-down-icon {
          animation: bounce 1.5s infinite;
        }
        @keyframes bounce {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0);
          }
        }
        .animate-bounce {
          animation: bounce 1.5s infinite;
        }

        /* small style for mute button to be visible on small screens */
        .mute-toggle-btn {
          margin-top: 15px;
          font-size: 14px;
          font-weight: 600;
        }

        /* minor visual polish when loaded */
        .hero-section.loaded .video-iframe,
        .hero-section.loaded .hero-skeleton-content,
        .hero-section.loaded h2,
        .hero-section.loaded p {
          animation: fadeIn 400ms ease both;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
